/**
 * アンケート回答の保存（視聴プレイヤー用ユーティリティ）。
 *
 * 公開 API `POST /public/scenarios/{id}/survey-responses` へ回答を都度送信する。
 * sessionId は analytics と共通の視聴セッションIDを使い、サーバ側で
 * `(sessionId, questionId)` の行集合置換により冪等に保存される（SINGLE/MULTI/FREE 共通）。
 * 送信失敗は視聴体験に影響させないため握りつぶす（analytics と同方針）。
 */

import {
  recordSurveyImpression as recordSurveyImpressionApi,
  submitSurveyForm as submitSurveyFormApi,
  submitSurveyResponse as submitSurveyResponseApi,
} from '@/orval/generated/public/scenarios/scenarios'
import {
  recordVideoSurveyImpression as recordVideoSurveyImpressionApi,
  submitVideoSurveyForm as submitVideoSurveyFormApi,
  submitVideoSurveyResponse as submitVideoSurveyResponseApi,
} from '@/orval/generated/public/videos/videos'
import { getOrCreateSessionId } from './analytics'

export interface TrackSurveyResponseParams {
  scenarioId: string
  nodeKey: string
  questionId: string
  /** 選択した選択肢ID。SINGLE=1件 / MULTI=複数 / FREE=空 */
  optionIds: string[]
  /** FREE 設問の自由記述（選択式は null） */
  freeText: string | null
  /** リード（PII）項目の設問で入力された値。専用テーブルへ隔離保存される */
  leadValue?: string | null
}

/**
 * アンケート回答を公開APIへ保存する。
 * 設問文・選択肢ラベルはサーバ側で解決するため送らない。
 * リード（PII）項目の設問では leadValue も送り、サーバ側で回答とは別テーブルへ保存される。
 */
export async function trackSurveyResponse({
  scenarioId,
  nodeKey,
  questionId,
  optionIds,
  freeText,
  leadValue,
}: TrackSurveyResponseParams): Promise<void> {
  try {
    const sessionId = getOrCreateSessionId()
    await submitSurveyResponseApi(scenarioId, {
      sessionId,
      nodeKey,
      questionId,
      optionIds,
      freeText: freeText ?? undefined,
      leadValue: leadValue ?? undefined,
    })
  } catch (error) {
    console.error('Failed to submit survey response:', error)
    // 回答送信の失敗はユーザー体験に影響しないため握りつぶす
  }
}

/** PII 混在フォーム（SURVEYノードの全画面ゲート）の1回答ぶん。 */
export interface SurveyFormAnswerInput {
  questionId: string
  /** 選択した選択肢ID。SINGLE=1件 / MULTI=複数 / 非選択式=空 */
  optionIds: string[]
  /** FREE 設問の自由記述（選択式・リード項目は null） */
  freeText: string | null
  /** リード（PII）項目の設問で入力された値（非PII設問は null） */
  leadValue: string | null
}

export interface SubmitSurveyFormParams {
  scenarioId: string
  nodeKey: string
  answers: SurveyFormAnswerInput[]
}

/**
 * PII 混在フォームを一括送信する（SURVEYノードの全画面ゲート・#264）。
 *
 * フォーム全体を1リクエストで公開APIへ送り、サーバ側で「PII→リード表 / 非PII→回答表」へ
 * 単一トランザクションで atomic に振り分け保存される（部分保存なし）。都度ではなく送信時のみ保存する。
 * sessionId は analytics と共通の視聴セッションIDを使う。送信失敗は視聴体験に影響させないため
 * 握りつぶす（trackSurveyResponse と同方針・権威的な必須ゲートはサーバにある）。
 */
export async function submitSurveyForm({
  scenarioId,
  nodeKey,
  answers,
}: SubmitSurveyFormParams): Promise<void> {
  try {
    const sessionId = getOrCreateSessionId()
    await submitSurveyFormApi(scenarioId, {
      sessionId,
      nodeKey,
      answers: answers.map((answer) => ({
        questionId: answer.questionId,
        optionIds: answer.optionIds,
        freeText: answer.freeText ?? undefined,
        leadValue: answer.leadValue ?? undefined,
      })),
    })
  } catch (error) {
    console.error('Failed to submit survey form:', error)
    // フォーム送信の失敗はユーザー体験に影響しないため握りつぶす
  }
}

/**
 * 動画詳細ページ（個別・関連動画付き）からアンケートを扱うユーティリティ（ADR-0015）。
 *
 * アンケートの持ち主は動画なので、シナリオ文脈が無くても動画単位の公開APIへ送れる。
 * `?from=` 付きの関連動画付きページでは流入元プレイリストも一緒に送り、サーバ側で
 * 帰属として記録される（発生元は動画詳細ページに分類される）。
 * 送信失敗は視聴体験に影響させないため握りつぶす（埋め込みと同方針）。
 */

export interface TrackVideoSurveyResponseParams {
  videoId: string
  scenarioId?: string | null
  questionId: string
  optionIds: string[]
  freeText: string | null
  leadValue?: string | null
}

export async function trackVideoSurveyResponse({
  videoId,
  scenarioId,
  questionId,
  optionIds,
  freeText,
  leadValue,
}: TrackVideoSurveyResponseParams): Promise<void> {
  try {
    const sessionId = getOrCreateSessionId()
    await submitVideoSurveyResponseApi(videoId, {
      sessionId,
      questionId,
      scenarioId: scenarioId ?? undefined,
      optionIds,
      freeText: freeText ?? undefined,
      leadValue: leadValue ?? undefined,
    })
  } catch (error) {
    console.error('Failed to submit video survey response:', error)
  }
}

export interface SubmitVideoSurveyFormParams {
  videoId: string
  scenarioId?: string | null
  answers: SurveyFormAnswerInput[]
}

export async function submitVideoSurveyForm({
  videoId,
  scenarioId,
  answers,
}: SubmitVideoSurveyFormParams): Promise<void> {
  try {
    const sessionId = getOrCreateSessionId()
    await submitVideoSurveyFormApi(videoId, {
      sessionId,
      scenarioId: scenarioId ?? undefined,
      answers: answers.map((answer) => ({
        questionId: answer.questionId,
        optionIds: answer.optionIds,
        freeText: answer.freeText ?? undefined,
        leadValue: answer.leadValue ?? undefined,
      })),
    })
  } catch (error) {
    console.error('Failed to submit video survey form:', error)
  }
}

/**
 * アンケートの提示到達を記録する（到達→完了ファネルの土台・ADR-0015）。
 * サーバ側で視聴セッション×テンプレ×紐づけ先に重複排除されるので、多重送信は無害。
 */
export async function trackSurveyImpression(params: {
  scenarioId: string
  nodeKey: string
}): Promise<void> {
  try {
    const sessionId = getOrCreateSessionId()
    await recordSurveyImpressionApi(params.scenarioId, {
      sessionId,
      nodeKey: params.nodeKey,
    })
  } catch (error) {
    console.error('Failed to record survey impression:', error)
  }
}

export async function trackVideoSurveyImpression(params: {
  videoId: string
  scenarioId?: string | null
}): Promise<void> {
  try {
    const sessionId = getOrCreateSessionId()
    await recordVideoSurveyImpressionApi(params.videoId, {
      sessionId,
      scenarioId: params.scenarioId ?? undefined,
    })
  } catch (error) {
    console.error('Failed to record video survey impression:', error)
  }
}
